// pages/auth/Login.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import { login, clearError } from "../../store/slices/authSlice";
import logo from "../../assets/images/logo.png";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { isLoading, error, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
    return () => {
      dispatch(clearError());
    };
  }, [isAuthenticated, navigate, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await dispatch(login({ email, password }));
    if (result.meta.requestStatus === "fulfilled") {
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3">
            <img
              src={logo}
              alt="AdSense Clone"
              className="h-28 w-auto"
              onError={(e) => {
                e.currentTarget.src =
                  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIiIGhlaWdodD0iMzIiIHZpZXdCb3g9IjAgMCAzMiAzMiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEwLjY2NjcgMjEuMzMzM0MxMC42NjY3IDIzLjAwNzIgMTEuNjI1NyAyNC41IDEzIDI0LjVIMTlWMjdIMTNDOS42ODkzIDI3IDcgMjQuMzEwNyA3IDIxVjExSDkuNjY2N1YyMS4zMzMzWiIgZmlsbD0iIzQyODVGOCIvPgo8cGF0aCBkPSJNMTkgNy42NjY2N0MyMS43NTU5IDcuNjY2NjcgMjQgOS45MTA3NSAyNCAxMi42NjY3VjI3SDE5VjEyLjY2NjdIMTMuNjY2N1Y3LjY2NjY3SDE5WiIgZmlsbD0iIzM0QTg1MyIvPgo8cGF0aCBkPSJNMjEgN0MyMSA1Ljg5NTQzIDIwLjEwNDYgNSAxOSA1SDEzQzExLjg5NTQgNSAxMSA1Ljg5NTQzIDExIDdWMTAuMzMzM0gxM1Y3SDE5VjEwLjMzMzNIMjFWN1oiIGZpbGw9IiNGRkJDMDUiLz4KPHBhdGggZD0iTTcgMTdDNyAxNS44OTU0IDcuODk1NDMgMTUgOSAxNUgxMy42NjY3VjE3SDlDNy44OTU0MyAxNyA3IDE2LjEwNDYgNyAxNVoiIGZpbGw9IiNFQTQzMzUiLz4KPC9zdmc+Cg==";
              }}
            />
          </div>
          <h1 className="mt-4 text-2xl font-normal text-[#202124]">Sign in</h1>
          <p className="mt-2 text-sm text-[#5f6368]">
            to continue to AdSense Clone
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg border border-[#dadce0] p-8">
          {error && (
            <div className="mb-4 p-3 bg-[#fce8e6] border border-[#f28b82] text-[#d33426] text-sm rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-[#dadce0] rounded hover:border-[#1a73e8] focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] outline-none transition"
                placeholder="Email address"
              />
            </div>

            <div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-[#dadce0] rounded hover:border-[#1a73e8] focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] outline-none transition"
                placeholder="Password"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 text-[#1a73e8] focus:ring-[#1a73e8] border-[#dadce0] rounded"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm text-[#5f6368]"
                >
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <a href="#" className="text-[#1a73e8] hover:text-[#0d62d9]">
                  Forgot password?
                </a>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-3 px-4 bg-[#1a73e8] text-white font-medium rounded hover:bg-[#0d62d9] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#1a73e8] ${
                  isLoading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </button>

              <Link
                to="/register"
                className="block w-full py-3 px-4 bg-white border border-[#dadce0] text-[#1a73e8] font-medium rounded hover:bg-[#f8f9fa] text-center"
              >
                Create account
              </Link>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-[#dadce0]">
            <p className="text-center text-sm text-[#5f6368]">
              Demo credentials:
            </p>
            <div className="mt-2 text-center text-xs text-[#5f6368] space-y-1">
              <p>Admin: admin@adsense.com / password</p>
              <p>User: user@adsense.com / password</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-[#5f6368]">
          <p>
            © {new Date().getFullYear()} AdSense Clone. All rights reserved.
          </p>
          <div className="mt-2 space-x-4">
            <a href="#" className="hover:text-[#202124]">
              Privacy
            </a>
            <a href="#" className="hover:text-[#202124]">
              Terms
            </a>
            <a href="#" className="hover:text-[#202124]">
              Help
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
